import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import logo from '../assets/cropped_circle_image.png';
import playStoreBadge from '../assets/play-store-badge.svg';
import appStoreBadge from '../assets/app-store-badge.svg';

// App Store and Play Store logos (placeholder images)
const PlayStoreLogo = () => (
  <Box
    component="img"
    src={playStoreBadge}
    alt="Get it on Google Play"
    sx={{ height: 40, width: 'auto' }}
  />
);

const AppStoreLogo = () => (
  <Box
    component="img"
    src={appStoreBadge}
    alt="Download on the App Store"
    sx={{ height: 40, width: 'auto' }}
  />
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Check if we're on contact page
  const isAuthPage = location.pathname === '/contact';

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 50;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: 'Home', path: '/' },
    { text: 'Businesses', path: '/business' },
    { text: 'Customers', path: '/customers' },
    { text: 'Contact', path: '/contact' },
  ];

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', my: 2 }}>
        <img
          src={logo}
          alt="10D Stores Logo"
          style={{ height: '60px', width: 'auto', marginRight: '12px' }}
        />
        <Typography variant="h5" sx={{ fontFamily: 'Manrope, sans-serif', fontWeight: 'bold', fontSize: '1.6rem', color: isAuthPage ? '#2d2d2d' : 'primary.main' }}>
          10D Stores
        </Typography>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} component={Link} to={item.path}>
            <ListItemText
              primary={item.text}
              sx={{
                '& .MuiListItemText-primary': {
                  fontWeight: 'bold',
                  fontSize: '1.3rem',
                  color: isAuthPage ? '#2d2d2d' : 'inherit'
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: isAuthPage ? 'rgba(255, 255, 255, 0.98)' : (scrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent'),
          backdropFilter: isAuthPage ? 'blur(10px)' : (scrolled ? 'blur(10px)' : 'none'),
          transition: 'all 0.3s ease',
          color: isAuthPage ? '#2d2d2d' : (scrolled ? 'primary.main' : 'white'),
          borderBottom: isAuthPage ? '1px solid rgba(0, 0, 0, 0.1)' : (scrolled ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'),
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          <Box
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <img
              src={logo}
              alt="10D Stores Logo"
              style={{ height: '70px', width: 'auto', marginRight: '16px' }}
            />
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 'bold',
                fontSize: '1.8rem',
                color: isAuthPage ? '#2d2d2d' : (scrolled ? 'primary.main' : 'white'),
                transition: 'color 0.3s ease',
              }}
            >
              10D Stores
            </Typography>
          </Box>

          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {navItems.map((item) => (
                <Button
                  key={item.text}
                  component={Link}
                  to={item.path}
                  sx={{
                    color: isAuthPage ? '#2d2d2d' : (scrolled ? 'primary.main' : 'white'),
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: '1.3rem',
                    transition: 'color 0.3s ease',
                    '&:hover': {
                      backgroundColor: isAuthPage ? 'rgba(227, 102, 45, 0.1)' : (scrolled ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.1)'),
                    }
                  }}
                >
                  {item.text}
                </Button>
              ))}
              <Button
                component={Link}
                to="/app"
                sx={{
                  textTransform: 'none',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: isAuthPage ? '#2d2d2d' : (scrolled ? 'primary.main' : 'white'),
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: isAuthPage ? 'rgba(227, 102, 45, 0.1)' : (scrolled ? 'rgba(25, 118, 210, 0.1)' : 'rgba(255, 255, 255, 0.1)'),
                  }
                }}
              >
                <PlayStoreLogo />
                <AppStoreLogo />
              </Button>
            </Box>
          )}

          {isMobile && (
            <IconButton
              color={isAuthPage ? '#2d2d2d' : 'inherit'}
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
